import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { searchItems, type SearchHit, type SearchItem } from '../../app/router/search'

/**
 * The chrome's search box, with something behind it.
 *
 * 🔴 THE INPUT THIS REPLACES HAD NO HANDLER. It was drawn on every adult screen
 * in the product with `placeholder="Search anything..."` and no state, no
 * results and no `onChange` — the same defect `DashboardShell` records for the
 * hamburger that opened no drawer: *"a control that looks like the way out and
 * is not is worse than no control: the reader stops looking for the real one."*
 *
 * 🔴 IT IS A COMBOBOX, NOT A TEXT FIELD WITH A DIV UNDER IT. Arrow keys move
 * through the results, Enter opens the highlighted one, Escape closes without
 * navigating, and `aria-activedescendant` tells a screen reader which row is
 * live. A results panel a keyboard cannot reach is a panel half this product's
 * users cannot use, on a control that sits on every screen.
 *
 * ⚠️ WHAT IT SEARCHES IS THE CALLER'S LIST, WHICH IS THE ROLE'S OWN NAV. See
 * `search.ts` for why that scope is the honest one and not a first step.
 */
export interface GlobalSearchProps {
  items: readonly SearchItem[]
  /**
   * Given the WHOLE chosen hit, not just its id. The caller navigates.
   *
   * ⚠️ IT USED TO BE THE ID ALONE, AND THAT ONLY WORKED WHILE EVERY RESULT WAS A
   * ROUTE. A record carries its own `href` — a screen plus a seeded filter — and
   * an id of `child:ck7…` is not a destination anything can navigate to.
   */
  onPick: (hit: SearchHit) => void
  /**
   * Called once, the first time the box is focused.
   *
   * 🔴 THIS IS WHAT KEEPS THE ROSTER OFF EVERY PAGE LOAD. The school corpus is
   * three whole-collection reads; doing them because a screen rendered would put
   * three requests behind every navigation in the tier, for a box most visits
   * never touch. Focus is the first moment the reader has said they want it.
   */
  onActivate?: () => void
  placeholder?: string
}

export function GlobalSearch({
  items,
  onPick,
  onActivate,
  placeholder = 'Search anything...',
}: GlobalSearchProps) {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [open, setOpen] = useState(false)
  const activated = useRef(false)
  const box = useRef<HTMLDivElement>(null)
  const listId = useId()

  const hits = useMemo(() => searchItems(items, query), [items, query])

  /*
   * ⚠️ CLAMPED WHENEVER THE LIST SHRINKS. Type "a", arrow to the fourth row,
   * type "tt" — without this the highlight sits past the end of a two-row list
   * and Enter navigates nowhere.
   */
  const index = hits.length === 0 ? -1 : Math.min(active, hits.length - 1)

  /* Pointer dismissal, matching every other menu in this shell. */
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('mousedown', onDown) }
  }, [open])

  const choose = (hit: SearchHit) => {
    onPick(hit)
    /* ⚠️ THE BOX EMPTIES ON NAVIGATE. Leaving the query in it means the next
       screen opens with a stale panel of results for a search already acted on. */
    setQuery('')
    setOpen(false)
    setActive(0)
  }

  const showPanel = open && query.trim() !== ''

  return (
    /*
     * ⚠️ `hidden md:block`, UNCHANGED FROM THE DEAD INPUT. At `sm` a fixed box
     * plus the bell plus the avatar is 380px of a 640px bar, and this is not a
     * control anyone reaches for on a phone — the page's own filters do the same
     * job. The width ramp is the shell's.
     */
    <div className="relative hidden w-40 md:block lg:w-64" ref={box}>
      <svg
        className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="search"
        role="combobox"
        aria-label="Search"
        aria-expanded={showPanel}
        aria-controls={showPanel ? listId : undefined}
        aria-activedescendant={
          showPanel && index >= 0 ? `${listId}-${String(index)}` : undefined
        }
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setActive(0)
          setOpen(true)
        }}
        onFocus={() => {
          setOpen(true)
          if (!activated.current) {
            activated.current = true
            onActivate?.()
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            /* ⚠️ ESCAPE CLOSES, IT DOES NOT NAVIGATE AND IT DOES NOT CLEAR. A
               person pressing it wants the page back, not their typing undone. */
            setOpen(false)
            return
          }
          if (hits.length === 0) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setActive((i) => (i + 1) % hits.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setOpen(true)
            setActive((i) => (i - 1 + hits.length) % hits.length)
          } else if (e.key === 'Enter') {
            e.preventDefault()
            const hit = hits[index]
            if (hit) choose(hit)
          }
        }}
        /* ⚠️ 44 REAL PIXELS UNDER A THUMB, `h-9` UNDER A MOUSE. `h-9` is 2.25rem,
           and `responsive.css` scales the root down to 0.85rem on a short
           viewport — so on a phone held sideways this field rendered 31px tall,
           the smallest control on any parent screen. A px floor is immune to that
           scale, which is the point: the root shrinks so more fits on screen, and
           a finger does not shrink with it. */
        className="w-full h-9 min-h-[44px] pointer-fine:min-h-0 pl-9 pr-4 rounded-xl border border-slate-200 bg-slate-50 text-dash-xs font-medium text-slate-800 placeholder-slate-400 outline-none focus:border-indigo-500 focus:bg-white transition-all"
      />

      {showPanel && (
        <div
          /*
           * ⚠️ WIDER THAN THE BOX AT `md`. The input is 160px there, which fits
           * "Announcements" and nothing beside it; the panel is free to be the
           * width its rows need because it is over the page rather than in the
           * bar.
           */
          className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-2xl"
        >
          {hits.length === 0 ? (
            /* 🔴 "Nothing here" IS AN ANSWER AND HAS TO BE GIVEN. A panel that
               renders nothing at all is indistinguishable from the box that used
               to do nothing at all. */
            <p className="px-3.5 py-3 font-dash text-dash-xs font-semibold text-slate-400">
              Nothing matches “{query.trim()}”.
            </p>
          ) : (
            <ul role="listbox" id={listId}>
              {hits.map((hit, i) => (
                <li key={hit.id}>
                  <button
                    type="button"
                    role="option"
                    id={`${listId}-${String(i)}`}
                    aria-selected={i === index}
                    /* `onMouseDown` rather than `onClick`: the input's blur would
                       otherwise close the panel before the click lands. */
                    onMouseDown={(e) => {
                      e.preventDefault()
                      choose(hit)
                    }}
                    onMouseEnter={() => { setActive(i) }}
                    className={`flex w-full flex-col items-start gap-0.5 px-3.5 py-2 text-left transition-colors ${
                      i === index ? 'bg-canvas' : ''
                    }`}
                  >
                    <span className="font-dash text-dash-xs font-extrabold text-ink">
                      {hit.label}
                    </span>
                    {hit.hint ? (
                      <span className="font-dash text-[11px] font-semibold text-muted">
                        {hit.hint}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
